/**
 * OperatorCard Component
 *
 * Polymorphic card that renders as <div>, <Link>, or <button> based on props.
 * Uses bg-op-card for dark mode support. Press feedback via active:scale.
 *
 * When both href and onClick are provided, href takes precedence (renders as Link).
 */

import { Link } from 'react-router-dom';
import { cn } from '../../../lib/utils';

interface OperatorCardProps {
  /** Renders as <Link> when provided */
  href?: string;
  /** Renders as <button> when provided (href takes precedence) */
  onClick?: () => void;
  /** Adds shadow-md elevation */
  raised?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Card content */
  children: React.ReactNode;
}

export function OperatorCard({
  href,
  onClick,
  raised,
  className,
  children,
}: OperatorCardProps) {
  const baseClasses = cn(
    'block rounded-2xl bg-op-card p-4 ring-1 ring-black/[0.04]',
    raised && 'shadow-md',
    (href || onClick) &&
      'active:scale-[0.98] transition-transform duration-150 cursor-pointer focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2',
    className,
  );

  if (href) {
    return (
      <Link to={href} className={baseClasses}>
        {children}
      </Link>
    );
  }

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={baseClasses}>
        {children}
      </button>
    );
  }

  return <div className={baseClasses}>{children}</div>;
}
